import React from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  LayoutDashboard, Users, Bell, Settings as SettingsIcon,
  Workflow, ListChecks, Video, Sparkles, Mail, GitBranch, LogOut, ShieldCheck, ScrollText,
  FileEdit, MessageSquare, Handshake, Menu,
} from 'lucide-react';
import { Avatar } from '@/components/Avatar';
import { classNames } from '@/lib/format';
import { useQuery } from '@tanstack/react-query';
import { v1Get } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { VersionBadge } from '@/components/VersionBadge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useMyPermissions } from '@/auth/permissions';
import { HireIqLogo } from '@/components/HireIqLogo';

const ROLE_LABELS = {
  admin: 'Admin',
  hr_manager: 'HR Manager',
  hr_recruiter: 'HR Recruiter',
  interviewer: 'Interviewer',
  hiring_manager: 'Hiring Manager',
};

const PRIMARY_NAV = [
  { to: '/portal', label: 'Dashboard', icon: LayoutDashboard, end: true, testid: 'nav-dashboard', need: 'dashboard.view' },
  { to: '/portal/candidates', label: 'Candidates', icon: Users, testid: 'nav-candidates', anyOf: ['candidate.view_all', 'candidate.view_assigned'] },
  { to: '/portal/resume-enrichment', label: 'Resume Enrichment', icon: FileEdit, testid: 'nav-resume-enrichment', need: 'candidate.bulk_enrichment' },
  { to: '/portal/notifications', label: 'Notifications', icon: Bell, testid: 'nav-notifications', need: 'notifications.view' },
];

// Sprint 15.1 — Per-item RBAC gating. Each settings entry declares the
// permission code it requires; the visibility filter resolves against the
// current user's effective permission set (with admin shortcut + legacy
// users.role fallback handled inside useMyPermissions / Gated).
const SETTINGS_NAV = [
  { to: '/portal/settings/users', label: 'Users', icon: ShieldCheck, testid: 'nav-settings-users', need: 'admin.users.manage' },
  { to: '/portal/settings/roles', label: 'Roles & Departments', icon: GitBranch, testid: 'nav-settings-roles', need: 'admin.master_data.manage' },
  { to: '/portal/settings/rbac', label: 'Roles & Permissions', icon: ShieldCheck, testid: 'nav-settings-rbac', anyOf: ['admin.rbac.view', 'admin.rbac.manage'] },
  { to: '/portal/settings/pipelines', label: 'Pipelines', icon: Workflow, testid: 'nav-settings-pipelines', need: 'pipeline.view' },
  { to: '/portal/settings/questions/videobot', label: 'Video Bot Bank', icon: Video, testid: 'nav-settings-videobot', need: 'bank.videobot.view' },
  { to: '/portal/settings/questions/mcq', label: 'IQ Assessment Bank', icon: ListChecks, testid: 'nav-settings-mcq', need: 'bank.psa.view' },
  { to: '/portal/settings/questions/tech', label: 'Tech Assessment Bank', icon: ListChecks, testid: 'nav-settings-tech', need: 'bank.tech.view' },
  { to: '/portal/settings/questions/panel', label: 'Tech Panel Bank', icon: MessageSquare, testid: 'nav-settings-panel', need: 'bank.panel.view' },
  { to: '/portal/settings/questions/offer', label: 'HR Panel Bank', icon: Handshake, testid: 'nav-settings-offer', need: 'bank.offer.view' },
  { to: '/portal/settings/ai-config', label: 'AI Report Config', icon: Sparkles, testid: 'nav-settings-ai', need: 'admin.ai_config.manage' },
  { to: '/portal/settings/resume-template', label: 'Resume Template', icon: ScrollText, testid: 'nav-settings-resume-template', need: 'admin.resume_template' },
  { to: '/portal/settings/email-templates', label: 'Email Templates', icon: Mail, testid: 'nav-settings-email', need: 'admin.email_templates' },
  { to: '/portal/settings/global', label: 'Global Settings', icon: SettingsIcon, testid: 'nav-settings-global', need: 'admin.global_settings' },
];

const NavItem = ({ to, label, icon: Icon, end, testid, trailing, onNavigate }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={testid}
    onClick={() => onNavigate?.()}
    className={({ isActive }) =>
      classNames(
        'group flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
        isActive
          ? 'bg-primary/10 text-primary'
          : 'text-foreground/70 hover:bg-accent hover:text-foreground',
      )
    }
  >
    <Icon className="h-4 w-4" strokeWidth={1.75} />
    <span className="flex-1 truncate">{label}</span>
    {trailing}
  </NavLink>
);

function SidebarContent({
  visiblePrimary,
  visibleSettings,
  unread,
  canViewNotifications,
  user,
  logout,
  onNavigate,
  showLogo = true,
}) {
  return (
    <>
      {showLogo && (
        <div className="flex h-28 shrink-0 items-center border-b px-3">
          <HireIqLogo
            size="sidebar"
            className="max-w-[228px]"
            data-testid="portal-brand-logo"
          />
        </div>
      )}
      <nav className="min-h-0 flex-1 space-y-6 overflow-y-auto p-3">
        <div className="space-y-1">
          {visiblePrimary.map(({ need: _need, anyOf: _anyOf, adminOnly: _adminOnly, ...n }) => (
            <NavItem
              key={n.to}
              {...n}
              onNavigate={onNavigate}
              trailing={
                n.to === '/portal/notifications' && canViewNotifications && unread > 0 ? (
                  <span
                    data-testid="sidebar-notif-badge"
                    className="ml-auto grid h-5 min-w-5 place-items-center rounded-full bg-rose-600 px-1 text-[10px] font-semibold text-white"
                  >
                    {unread > 99 ? '99+' : unread}
                  </span>
                ) : null
              }
            />
          ))}
        </div>
        <div>
          <p className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Administration</p>
          <div className="space-y-1">
            {visibleSettings.map(({ need: _need, anyOf: _anyOf, adminOnly: _adminOnly, ...n }) => (
              <NavItem key={n.to} {...n} onNavigate={onNavigate} />
            ))}
          </div>
        </div>
      </nav>
      <div className="shrink-0 space-y-2 border-t px-3 pt-3 pb-2" data-testid="sidebar-version-row">
        <VersionBadge />
      </div>
      <div className="shrink-0 border-t p-3">
        <div className="flex items-center gap-3 rounded-lg p-2" data-testid="user-bar">
          <Avatar name={user?.full_name || '—'} size={36} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold" data-testid="user-name">{user?.full_name || '—'}</p>
            <div className="flex items-center gap-1.5">
              <p className="truncate text-[11px] text-muted-foreground" data-testid="user-email">{user?.email || '—'}</p>
              {user?.role && (
                <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-primary" data-testid="user-role">
                  {ROLE_LABELS[user.role] || user.role}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            data-testid="logout-button"
            onClick={logout}
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            aria-label="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  );
}

export default function PortalLayout() {
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const { user, logout, hasRole } = useAuth();

  // Sprint 15.1 — resolve effective permissions for sidebar gating.
  // Admin shortcut + legacy users.role fallback are handled inside the hook.
  const { data: myPerms } = useMyPermissions();
  const grantedCodes = React.useMemo(() => new Set(myPerms?.permission_codes || []), [myPerms]);
  const isAdminEffective =
    myPerms?.legacy_role === 'admin' || (myPerms?.roles || []).some((r) => r.code === 'admin');
  const canViewNotifications = isAdminEffective || grantedCodes.has('notifications.view');

  const { data: notifs } = useQuery({
    queryKey: ['v1-notifications', 'unread-count'],
    queryFn: () => v1Get('/notifications?limit=1'),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    enabled: canViewNotifications,
  });
  const unread = notifs?.unread_count || 0;

  const navVisible = (n) => {
    if (n.adminOnly && !isAdminEffective && !hasRole('admin')) return false;
    if (n.anyOf && !isAdminEffective && !n.anyOf.some((c) => grantedCodes.has(c))) return false;
    if (n.need && !isAdminEffective && !grantedCodes.has(n.need)) return false;
    return true;
  };
  const visiblePrimary = PRIMARY_NAV.filter(navVisible);
  const visibleSettings = SETTINGS_NAV.filter(navVisible);
  const closeMobileNav = React.useCallback(() => setMobileNavOpen(false), []);
  const sidebarProps = {
    visiblePrimary,
    visibleSettings,
    unread,
    canViewNotifications,
    user,
    logout,
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] text-foreground">
      {/* minmax(0,1fr)+min-w-0: contain wide children (tables) so the page does not scroll horizontally */}
      <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)]">
        <aside className="hidden border-r bg-[hsl(210_40%_99%)] lg:sticky lg:top-0 lg:flex lg:h-screen lg:flex-col">
          <SidebarContent {...sidebarProps} />
        </aside>

        <main className="flex min-h-screen min-w-0 flex-col">
          <header className="sticky top-0 z-40 flex items-center justify-between border-b bg-[hsl(210_40%_99%)] px-4 py-3 lg:hidden">
            <HireIqLogo
              size="sm"
              className="max-w-[140px]"
              data-testid="mobile-brand-logo"
            />
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  data-testid="mobile-nav-toggle"
                  aria-label="Open navigation menu"
                  className="rounded-md p-2 text-foreground transition-colors hover:bg-accent"
                >
                  <Menu className="h-5 w-5" strokeWidth={1.75} />
                </button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="flex w-[280px] max-w-[85vw] flex-col gap-0 p-0 sm:max-w-[280px]"
                data-testid="mobile-nav-sheet"
              >
                <SheetHeader className="sr-only">
                  <SheetTitle>Navigation menu</SheetTitle>
                </SheetHeader>
                <SidebarContent {...sidebarProps} showLogo={false} onNavigate={closeMobileNav} />
              </SheetContent>
            </Sheet>
          </header>
          <div className="min-w-0 flex-1">
            <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
